import React, { useEffect, useRef } from "react";
import GooglePlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import "../styles/GoogleMapInput.css";
import { shouldRenderAutoRequiredMark } from "../utils/requiredLabel";
import { loadGoogleMaps } from "../utils/googleMapsLoader";

const GoogleMapInput = ({
    label,
    name,
    value,
    onChange,
    onBlur: formikOnBlur,
    error,
    touched,
    placeholder = "Search location...",
    required = true,
    fieldPrefix = "dispatch",
    isDisabled = false,
    setFieldValue,
    storeAs = "object",
    latitudeFieldName = "",
    longitudeFieldName = "",
}) => {
    const getKey = (suffix) => fieldPrefix ? `${fieldPrefix}_${suffix}` : suffix;
    const pointKey = getKey("point");
    const latitudeKey = getKey("latitude");
    const longitudeKey = getKey("longitude");

    const apiKey = (process.env.REACT_APP_API_GOOGLE_MAP_KEY || "").trim();
    const typedTextRef = useRef("");

    useEffect(() => {
        if (apiKey && !window.google?.maps?.places) {
            loadGoogleMaps(apiKey).catch((err) => {
                console.warn("loadGoogleMaps warning in GoogleMapInput:", err);
            });
        }
    }, [apiKey]);

    const styles = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            background: 'transparent',
            border: 'none',
            fontSize: '13px',
            marginTop: '0px',
            cursor: isDisabled ? "not-allowed" : "text",
            boxShadow: 'none',
            minHeight: "28px",
            '&:hover': {
                border: 'none',
            },
        }),
        placeholder: (baseStyles) => ({
            ...baseStyles,
            color: '#C4C0D0',
            fontSize: '13px',
            fontWeight: '400',
        }),
        container: (baseStyles) => ({
            ...baseStyles,
            width: "100%",
        }),
        valueContainer: (baseStyles) => ({
            ...baseStyles,
            padding: '0',
        }),
        input: (baseStyles) => ({
            ...baseStyles,
            margin: '0',
            padding: '0',
            fontSize: '13px',
        }),
        singleValue: (baseStyles) => ({
            ...baseStyles,
            margin: '0',
            fontSize: '13px',
            color: isDisabled ? '#888' : '#333',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            padding: '0',
            color: '#000',
        }),
        clearIndicator: (baseStyles) => ({
            ...baseStyles,
            padding: '0 4px',
            cursor: 'pointer',
            color: '#888',
            '&:hover': {
                color: '#333',
            },
        }),
        menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: 99999,
        }),
        menuPortal: (baseStyles) => ({
            ...baseStyles,
            zIndex: 99999,
        }),
    };

    const updateValue = (address, latitude = "", longitude = "", rawResult = null) => {
        if (storeAs === "string") {
            if (typeof onChange === "function") onChange(address, latitude, longitude, rawResult);
            if (typeof setFieldValue === "function") setFieldValue(name, address);
            if (typeof setFieldValue === "function" && latitudeFieldName) {
                setFieldValue(latitudeFieldName, latitude);
            }
            if (typeof setFieldValue === "function" && longitudeFieldName) {
                setFieldValue(longitudeFieldName, longitude);
            }
            return;
        }

        const nextValue = {
            [pointKey]: address,
            [latitudeKey]: latitude,
            [longitudeKey]: longitude,
        };

        if (typeof onChange === "function") onChange(nextValue, rawResult);
        if (typeof setFieldValue === "function") setFieldValue(name, nextValue);
    };

    const clearValue = () => {
        typedTextRef.current = "";
        if (storeAs === "string") {
            updateValue("");
            return;
        }
        updateValue("", "", "");
    };

    const handleAddressChange = (selected) => {
        typedTextRef.current = "";
        if (!selected) {
            clearValue();
            return;
        }

        const address = selected.label;

        if (storeAs === "string" && !latitudeFieldName && !longitudeFieldName) {
            updateValue(address);
            return;
        }

        updateValue(address);

        geocodeByAddress(address)
            .then((results) => {
                if (results && results[0]) {
                    return Promise.all([results[0], getLatLng(results[0])]);
                }
                throw new Error("No geocode results found");
            })
            .then(([result, { lat, lng }]) => {
                updateValue(address, String(lat), String(lng), result);
            })
            .catch((e) => console.error("Geolocation error:", e));
    };

    const handleInputChange = (newValue, actionMeta) => {
        if (actionMeta.action === "input-change") {
            typedTextRef.current = newValue;
        }
    };

    const handleBlur = (e) => {
        if (typeof formikOnBlur === "function") {
            formikOnBlur(e);
        }

        const typed = (typedTextRef.current || "").trim();
        // If user typed something and blurred without selecting an item from the dropdown,
        // preserve the typed address so it doesn't vanish:
        if (typed && typed !== selectedValue) {
            updateValue(typed);
            geocodeByAddress(typed)
                .then((results) => {
                    if (results && results[0]) {
                        return Promise.all([results[0], getLatLng(results[0])]);
                    }
                    throw new Error("No geocode results found");
                })
                .then(([result, { lat, lng }]) => {
                    updateValue(typed, String(lat), String(lng), result);
                })
                .catch(() => {});
        }
    };

    const selectedValue =
        storeAs === "string"
            ? String(value || "").trim()
            : String(value?.[pointKey] || "").trim();

    const shouldShowAutoRequired = shouldRenderAutoRequiredMark(label, required);

    const errorMessage = typeof error === 'string'
        ? error
        : (error?.[pointKey] || error?.[getKey('point')]);

    const isTouched = typeof touched === 'boolean'
        ? touched
        : Boolean(touched && (touched?.[pointKey] || touched?.[getKey('point')] || touched === true));

    return (
        <div className='input_wrapper'>
            <div className={`input_container ${isDisabled ? 'disabled' : ''}`}>
                <label>
                    {label}
                    {shouldShowAutoRequired && <span style={{ color: "red" }}> *</span>}
                </label>

                <GooglePlacesAutocomplete
                    apiKey={apiKey}
                    autocompletionRequest={{ componentRestrictions: { country: ["pk"] } }}
                    selectProps={{
                        placeholder,
                        value: selectedValue
                            ? { label: selectedValue, value: selectedValue }
                            : null,
                        onChange: handleAddressChange,
                        onInputChange: handleInputChange,
                        onBlur: handleBlur,
                        styles: styles,
                        menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
                        menuPosition: 'fixed',
                        isClearable: true,
                        isDisabled,
                    }}
                />
            </div>
            {isTouched && errorMessage && (
                <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default GoogleMapInput;
